import { Trans, useTranslation } from 'react-i18next'
import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, FormLabel, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { CustomSwitch } from '../atoms/CustomSwitch'
import { Dropdown } from '../atoms/Dropdown'
import { lang } from '../../utils/lang'
import { setCaretPosition } from '../../utils/misc'

/**
 * Tehomaksu (power-based grid fee) settings, stored in the optimizer port (0x210) conf:
 * `peak_price` (c/kW/month, 0 = off), `enable_power_limit` + `power_limit` (free power, W),
 * `peak_lookback_months`, `peak_hour_from/to` (local hours, equal = all day),
 * `peak_weekday_mask` (bit0 = Monday) and `peak_month_mask` (bit0 = January), where a
 * zero mask means all. Here the masks are the selected days/months, all bits set = all.
 */
export interface PowerFeeSettings {
  /** An optimizer port exists to store the settings in. */
  enabled: boolean
  /** The grid operator charges a power fee. */
  on: boolean
  /** Price, cents per kW per month. */
  priceCents: number
  /** Free power (peaks up to this are free), W. */
  freeW: number
  /** Months a peak stays billed: 1 = calendar month, 12 = 12-month ratchet. */
  lookbackMonths: number
  hourFrom: number
  hourTo: number
  /** Selected weekdays, bit0 = Monday; 0x7F = every day, 0 = none (invalid). */
  weekdays: number
  /** Selected months, bit0 = January; 0xFFF = every month, 0 = none (invalid). */
  months: number
}

export const ALL_WEEKDAYS = 0x7F
export const ALL_MONTHS = 0xFFF

/** Prefill when the fee is switched on: validated by year simulations as safe without a tariff. */
export const POWER_FEE_DEFAULTS = {
  priceCents: 50,
  freeW: 8000,
  lookbackMonths: 1,
  hourFrom: 0,
  hourTo: 0,
  weekdays: ALL_WEEKDAYS,
  months: ALL_MONTHS,
}

// The wire carries the price as uint16 cents.
export const POWER_FEE_PRICE_MAX = 655.35
export const POWER_FEE_FREE_KW_MAX = 10000

export interface OptimizerPeakConfig {
  enable_power_limit: boolean
  power_limit: number
  peak_price: number
  peak_hour_from: number
  peak_hour_to: number
  peak_weekday_mask: number
  peak_lookback_months: number
  peak_month_mask: number
}

/**
 * Read the power fee settings from an optimizer conf. A conf without a price is off and
 * gets the defaults, which the form shows when the fee is switched on.
 */
export function powerFeeFromConfig(config: Partial<OptimizerPeakConfig>, enabled: boolean = true): PowerFeeSettings {
  const price = config.peak_price || 0
  if (price <= 0) {
    return { enabled, on: false, ...POWER_FEE_DEFAULTS }
  }
  return {
    enabled,
    on: true,
    priceCents: price,
    freeW: config.enable_power_limit ? (config.power_limit || 0) : 0,
    lookbackMonths: Math.max(config.peak_lookback_months || 0, 1),
    hourFrom: config.peak_hour_from || 0,
    hourTo: config.peak_hour_to || 0,
    weekdays: config.peak_weekday_mask || ALL_WEEKDAYS,
    months: config.peak_month_mask || ALL_MONTHS,
  }
}

/**
 * The optimizer conf fields for the settings. Off clears the fee (`peak_price` 0 turns the
 * firmware feature off) and leaves `power_limit` as it is.
 */
export function powerFeeToConfig(s: PowerFeeSettings): Partial<OptimizerPeakConfig> {
  if (!s.on) {
    return {
      enable_power_limit: false,
      peak_price: 0,
      peak_hour_from: 0,
      peak_hour_to: 0,
      peak_weekday_mask: 0,
      peak_lookback_months: 0,
      peak_month_mask: 0,
    }
  }
  return {
    enable_power_limit: true,
    power_limit: Math.round(s.freeW),
    peak_price: Math.round(s.priceCents),
    peak_hour_from: s.hourFrom,
    peak_hour_to: s.hourTo,
    peak_weekday_mask: s.weekdays === ALL_WEEKDAYS ? 0 : s.weekdays,
    peak_lookback_months: s.lookbackMonths,
    peak_month_mask: s.months === ALL_MONTHS ? 0 : s.months,
  }
}

export function isValidPowerFeePrice(euros: number): boolean {
  return Number.isFinite(euros) && euros > 0 && euros <= POWER_FEE_PRICE_MAX
}

export function isValidFreePower(kw: number): boolean {
  return Number.isFinite(kw) && kw >= 0 && kw <= POWER_FEE_FREE_KW_MAX
}

/**
 * Whether the settings can be saved: only checked while the fee is on.
 */
export function isValidPowerFeeSettings(s: PowerFeeSettings): boolean {
  return !s.on || (
    isValidPowerFeePrice(s.priceCents / 100) &&
    isValidFreePower(s.freeW / 1000) &&
    (s.weekdays & ALL_WEEKDAYS) !== 0 &&
    (s.months & ALL_MONTHS) !== 0
  )
}

// Text shown in a number field: at most 2 decimals, no trailing zeros.
const fmt = (value: number): string => `${parseFloat(value.toFixed(2))}`

export interface PowerFeeFormProps {
  settings: PowerFeeSettings
  onChange: (settings: PowerFeeSettings) => void
}

export function PowerFeeForm(props: PowerFeeFormProps) {
  const { settings, onChange } = props
  const { t } = useTranslation()
  const [price, setPrice] = useState(fmt(settings.priceCents / 100))
  const [freeKw, setFreeKw] = useState(fmt(settings.freeW / 1000))

  useEffect(() => {
    setPrice(fmt(settings.priceCents / 100))
    setFreeKw(fmt(settings.freeW / 1000))
  }, [settings])

  const toggle = () => onChange(settings.on
    ? { ...settings, on: false }
    : { ...settings, on: true, ...POWER_FEE_DEFAULTS })

  const toggleBit = (mask: number, bit: number) => mask ^ (1 << bit)

  const hours: Record<string, string> = {}
  for (let h = 0; h < 24; h++) {
    hours[`${h}`] = `${h < 10 ? '0' : ''}${h}:00`
  }

  const lookbacks: Record<string, string> = {
    1: t('Highest hour of the month'),
    12: t('Highest hour of 12 months'),
  }
  if (!(settings.lookbackMonths in lookbacks)) {
    lookbacks[settings.lookbackMonths] = t('Highest hour of {{months}} months', { months: settings.lookbackMonths })
  }

  const priceValid = isValidPowerFeePrice(parseFloat(price))
  const freeValid = isValidFreePower(parseFloat(freeKw))

  return <Box>
    <FormControl component="div">
      <Box>
        <CustomSwitch
          disabled={!settings.enabled}
          checked={settings.on}
          onClick={toggle}
          data-cy="switch-Power Fee"
          sx={{ mr: '1rem' }}
        />
        <Trans>Grid operator charges a power fee</Trans>
      </Box>
    </FormControl>

    {settings.on && <Box sx={{ ml: 2 }} data-cy="box-Power Fee">
      <FormHelperText>
        <Trans>If the grid operator bills the mean of the two highest hours or months, enter half of the price.</Trans>
      </FormHelperText>

      <FormControl component="div" sx={{ mr: 2 }}>
        <TextField
          data-cy="text-Power fee price"
          id="power-fee-price"
          margin="normal"
          label={t('Power fee (€/kW/month)')}
          type="number"
          value={price}
          onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
          onChange={(e) => setPrice(e.target.value)}
          onBlur={() => onChange({ ...settings, priceCents: price ? Math.round(parseFloat(price) * 100) : 0 })}
          error={!priceValid}
          inputProps={{ lang: 'fi', min: 0, step: 0.01 }} // fix decimal separator for Firefox
        />
        {!priceValid && <FormHelperText error>
          {t('The power fee must be between {{min}} and {{max}} €/kW/month', { min: 0.01, max: POWER_FEE_PRICE_MAX })}
        </FormHelperText>}
      </FormControl>

      <FormControl component="div">
        <TextField
          data-cy="text-Free power"
          id="power-fee-free"
          margin="normal"
          label={t('Free power (kW)')}
          type="number"
          value={freeKw}
          onFocus={(e) => setCaretPosition(e.target.id, e.target.value.length)}
          onChange={(e) => setFreeKw(e.target.value)}
          onBlur={() => onChange({ ...settings, freeW: freeKw ? Math.round(parseFloat(freeKw) * 1000) : 0 })}
          error={!freeValid}
          inputProps={{ lang: 'fi', min: 0, step: 0.1 }}
        />
        {!freeValid && <FormHelperText error>
          {t('The free power must be between {{min}} and {{max}} kW', { min: 0, max: POWER_FEE_FREE_KW_MAX })}
        </FormHelperText>}
      </FormControl>

      <Box sx={{ mt: 2 }}>
        <Dropdown
          label={t('Billing period')}
          items={lookbacks}
          selected={`${settings.lookbackMonths}`}
          onSelect={(v) => onChange({ ...settings, lookbackMonths: parseInt(v) })}
        />
      </Box>

      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Dropdown
          label={t('Counted hours from')}
          items={hours}
          selected={`${settings.hourFrom}`}
          onSelect={(v) => onChange({ ...settings, hourFrom: parseInt(v) })}
        />
        <Dropdown
          label={t('Counted hours until')}
          items={hours}
          selected={`${settings.hourTo}`}
          onSelect={(v) => onChange({ ...settings, hourTo: parseInt(v) })}
        />
      </Box>
      <FormHelperText>
        <Trans>Local time. The same start and end means all day.</Trans>
      </FormHelperText>

      <FormControl component="fieldset" sx={{ mt: 2, display: 'block' }} error={!(settings.weekdays & ALL_WEEKDAYS)}>
        <FormLabel component="legend"><Trans>Counted days</Trans></FormLabel>
        {[0, 1, 2, 3, 4, 5, 6].map(bit => <FormControlLabel
          key={bit}
          label={lang.dayNameShort(bit + 1)}
          control={<Checkbox
            size="small"
            data-cy={`checkbox-Weekday ${bit + 1}`}
            checked={(settings.weekdays & (1 << bit)) !== 0}
            onChange={() => onChange({ ...settings, weekdays: toggleBit(settings.weekdays, bit) })}
          />}
        />)}
        {!(settings.weekdays & ALL_WEEKDAYS) && <FormHelperText><Trans>Select at least one.</Trans></FormHelperText>}
      </FormControl>

      <FormControl component="fieldset" sx={{ mt: 2, display: 'block' }} error={!(settings.months & ALL_MONTHS)}>
        <FormLabel component="legend"><Trans>Counted months</Trans></FormLabel>
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(bit => <FormControlLabel
          key={bit}
          label={lang.monthNameShort(bit + 1)}
          control={<Checkbox
            size="small"
            data-cy={`checkbox-Month ${bit + 1}`}
            checked={(settings.months & (1 << bit)) !== 0}
            onChange={() => onChange({ ...settings, months: toggleBit(settings.months, bit) })}
          />}
        />)}
        {!(settings.months & ALL_MONTHS) && <FormHelperText><Trans>Select at least one.</Trans></FormHelperText>}
      </FormControl>
    </Box>}
  </Box>
}
